import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TriangleAlert } from 'lucide-react';
import { DeviceType, VersionEnforcement } from '@/lib/shared-types';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import type { DeviceVersionPolicy, UpsertVersionPolicyBody } from '../fleetApi';
import { compareVersions } from '../fleetHealth';

const DEVICE_TYPES = Object.values(DeviceType);
const ENFORCEMENTS = Object.values(VersionEnforcement);

/** The shape the server accepts: dotted numeric, optionally with a suffix. */
const VERSION_PATTERN = /^\d+(\.\d+)*([-+][0-9A-Za-z.-]+)?$/;

/**
 * Set the supported version floor for one device type.
 *
 * The version fields are validated against the same pattern the DTO uses, for
 * the reason the DTO validates them at all: an unparseable version cannot be
 * allowed to quietly become UNKNOWN and sit outside every compliance count.
 *
 * Two consistency checks happen here rather than at the server, because both are
 * about the operator's intent rather than the data's validity:
 *
 *  - a recommended version **below** the minimum is almost certainly the two
 *    fields typed the wrong way round, and it would make every device
 *    simultaneously above the recommendation and below the floor;
 *  - choosing BLOCK draws the consequence before the save, because the refusal
 *    that follows — and its `force` override — is easier to reason about when
 *    the operator already knows what BLOCK means. It refuses the *heartbeat*,
 *    and deliberately not PIN login: taking a venue's tills out of the payment
 *    flow is a bigger outage than the one the policy prevents.
 */
export default function VersionPolicyDialog({
  open,
  policy,
  takenTypes,
  onClose,
  onSave,
}: {
  open: boolean;
  policy: DeviceVersionPolicy | null;
  /** Types that already have a policy — the upsert would replace theirs. */
  takenTypes: DeviceType[];
  onClose: () => void;
  onSave: (body: UpsertVersionPolicyBody) => Promise<boolean>;
}) {
  const { t } = useTranslation();

  const [deviceType, setDeviceType] = useState<DeviceType>('POS');
  const [minSupportedVersion, setMinSupportedVersion] = useState('');
  const [recommendedVersion, setRecommendedVersion] = useState('');
  const [enforcement, setEnforcement] = useState<VersionEnforcement>('WARN');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setDeviceType(policy?.deviceType ?? 'POS');
    setMinSupportedVersion(policy?.minSupportedVersion ?? '');
    setRecommendedVersion(policy?.recommendedVersion ?? '');
    setEnforcement(policy?.enforcement ?? 'WARN');
    setNotes(policy?.notes ?? '');
  }, [open, policy]);

  const minValid = !minSupportedVersion || VERSION_PATTERN.test(minSupportedVersion.trim());
  const recommendedValid =
    !recommendedVersion || VERSION_PATTERN.test(recommendedVersion.trim());
  const recommendedBelowMin =
    minValid &&
    recommendedValid &&
    Boolean(minSupportedVersion && recommendedVersion) &&
    (compareVersions(recommendedVersion.trim(), minSupportedVersion.trim()) ?? 0) < 0;

  const submit = async () => {
    if (!minSupportedVersion.trim()) {
      setError(t('fleet.versions.minRequired'));
      return;
    }
    if (!minValid || !recommendedValid) {
      setError(t('fleet.versions.versionFormat'));
      return;
    }
    if (recommendedBelowMin) {
      setError(t('fleet.versions.recommendedBelowMin'));
      return;
    }
    setBusy(true);
    setError(null);
    const saved = await onSave({
      deviceType,
      minSupportedVersion: minSupportedVersion.trim(),
      recommendedVersion: recommendedVersion.trim() || undefined,
      enforcement,
      notes: notes.trim() || undefined,
    });
    setBusy(false);
    if (saved) onClose();
  };

  const replacing = !policy && takenTypes.includes(deviceType);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={policy ? t('fleet.versions.editTitle') : t('fleet.versions.addTitle')}
      description={t('fleet.versions.formHint')}
      className="max-w-xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label={t('fleet.common.type')} required hint={replacing ? t('fleet.versions.replaces') : undefined}>
          <Select
            value={deviceType}
            disabled={Boolean(policy)}
            onChange={(event) => setDeviceType(event.target.value as DeviceType)}
          >
            {DEVICE_TYPES.map((option) => (
              <option key={option} value={option}>
                {t(`fleet.deviceType.${option}`)}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('fleet.versions.minSupported')}
            required
            hint={t('fleet.versions.minHint')}
            error={minValid ? undefined : t('fleet.versions.versionFormat')}
          >
            <Input
              value={minSupportedVersion}
              onChange={(event) => setMinSupportedVersion(event.target.value)}
              placeholder="2.4.0"
              maxLength={40}
            />
          </Field>

          <Field
            label={t('fleet.versions.recommended')}
            hint={t('fleet.versions.recommendedHint')}
            error={
              !recommendedValid
                ? t('fleet.versions.versionFormat')
                : recommendedBelowMin
                  ? t('fleet.versions.recommendedBelowMin')
                  : undefined
            }
          >
            <Input
              value={recommendedVersion}
              onChange={(event) => setRecommendedVersion(event.target.value)}
              placeholder="2.6.0"
              maxLength={40}
            />
          </Field>
        </div>

        <Field label={t('fleet.versions.enforcement')} hint={t('fleet.versions.enforcementHint')}>
          <Select
            value={enforcement}
            onChange={(event) => setEnforcement(event.target.value as VersionEnforcement)}
          >
            {ENFORCEMENTS.map((option) => (
              <option key={option} value={option}>
                {t(`fleet.enforcement.${option}`)}
              </option>
            ))}
          </Select>
        </Field>

        {enforcement === 'BLOCK' && (
          <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50/70 p-3 text-sm dark:border-red-900 dark:bg-red-950/30">
            <Icon as={TriangleAlert} className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
            <div>
              <p className="font-medium">{t('fleet.versions.blockMeans')}</p>
              <p className="text-ink-muted">{t('fleet.versions.blockExplainer')}</p>
            </div>
          </div>
        )}

        <Field label={t('fleet.versions.notes')} hint={t('fleet.versions.notesHint')}>
          <Textarea
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            maxLength={500}
          />
        </Field>

        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
